import { CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useBets } from "../../common/contexts/betContext";
import { useFixtures } from "../../common/contexts/fixturesContext";
import { useUser } from "../../common/contexts/userContext";
import { AuthData, googleLogin } from "../../services/authService";
import { setSession } from "../../services/authStorage";
import { getUserBets } from "../../services/betService";
import { getAllMatches } from "../../services/matchService";
import "./MobileSign.scss";

export interface MobileAuthenticationPanelProps {}

// Google's button needs an explicit pixel width (it can't be 100%). GIS clamps
// it to 200–400px, so match the container width within that range.
const GOOGLE_BTN_MIN = 200;
const GOOGLE_BTN_MAX = 400;

export const MobileAuthenticationPanel: React.FC<
  MobileAuthenticationPanelProps
> = () => {
  const navigate = useNavigate();

  const { setUser } = useUser();
  const { setMatches } = useFixtures();
  const { setBets } = useBets();

  // Measure the wrapper so the button fits its container (form card) instead
  // of overflowing it.
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number>();
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setButtonWidth(
        Math.min(GOOGLE_BTN_MAX, Math.max(GOOGLE_BTN_MIN, el.clientWidth))
      );
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  async function handleGoogleLogin(credential: string) {
    try {
      const data = await googleLogin({ credential });
      await onGoogleLoginSuccess(data);
    } catch {
      alert("User couldn't be created - try once more.");
    }
  }

  const onGoogleLoginSuccess = async (data: { data: AuthData }) => {
    setSession(data.data.accessToken);
    setUser(data.data.user);
    // Hydrate fixtures and bets just like the username/password flow — the
    // app-level bootstrap only runs once on mount, so it won't fetch these
    // after an in-page Google login and "upcoming matches" would stay empty.
    const [matches, bets] = await Promise.all([
      getAllMatches(),
      getUserBets(data.data.user.id),
    ]);
    setMatches(matches.filter((match) => match.homeTeam && match.awayTeam));
    setBets(bets);
    navigate("/profile");
  };

  const onSuccess = (resp: CredentialResponse) => {
    if (!resp.credential) return;
    handleGoogleLogin(resp.credential);
  };

  const onError = () => {
    alert("Google login failed - try once more.");
  };

  // const facebookLink = useRef<HTMLAnchorElement | null>(null);

  // useEffect(() => {
  //   if (facebookLink.current) {
  //     facebookLink.current.click()
  //   }
  // }, [facebookLink.current])

  // if (navigator.userAgent.toLowerCase().includes('facebook')) {
  //   return (
  //     <a ref={facebookLink} href={window.location.href} target='_blank' download>Open in browser</a>
  //   )
  // }

  // if (navigator.userAgent.toLowerCase().includes('messenger')) {
  //   return (
  //     <a ref={facebookLink} href={window.location.href} target='_blank' download>Open in browser</a>
  //   )
  // }

  return (
    <div ref={containerRef} className="buttonGoogleStyles">
      {buttonWidth && (
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onError}
          width={buttonWidth}
        ></GoogleLogin>
      )}
    </div>
  );
};
